import { IconButton } from "@ameelio/ui";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import React from "react";
import { useTranslation } from "react-i18next";
import { Correspondent } from "../../api/graphql";
import MicroElapsedTime from "../../lib/MicroElapsedTime";
import TruncatedTypography from "../../lib/TruncatedTypography";
import { ChatBubble, ChatHeader } from "./ChatComponents";

type Props = {
  correspondent: Pick<Correspondent, "id" | "fullName">;
  currentUserId: string;
  contents?: string;
  children?: React.ReactNode;
  timestamp: Date;
  iconSlot: React.ReactNode;
  onClose?: () => void;
};

export default function ChatMessage({
  currentUserId,
  correspondent: user,
  contents,
  children,
  timestamp,
  iconSlot,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const from = user.id === currentUserId ? "me" : "them";

  const header = (
    <ChatHeader>
      {iconSlot}
      <TruncatedTypography
        variant="caption"
        sx={{ color: "text.primary", ml: 0.5, flexGrow: 1 }}
      >
        {from === "me" ? t("you") : user.fullName}
      </TruncatedTypography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        <MicroElapsedTime timestamp={timestamp} />
      </Typography>
    </ChatHeader>
  );

  const body = (
    <Stack
      direction="row"
      sx={{
        alignItems: "flex-start",
        gap: 1,
        justifyContent: "space-between",
      }}
    >
      {children || (
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
          }}
        >
          {contents}
        </Typography>
      )}
      {onClose && (
        <IconButton
          tooltip
          ariaLabel={t("Close")}
          onClick={onClose}
          sx={{ height: 24, width: 24 }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Stack>
  );

  return (
    <ChatBubble
      sx={{
        background: (theme) =>
          from === "me" ? alpha(theme.palette.primary.main, 0.3) : grey[200],
      }}
    >
      {body}
      {header}
    </ChatBubble>
  );
}
